import every from "lodash/every";
import first from "lodash/first";
import isArray from "lodash/isArray";
import { getProperties } from "PFCore/helpers/templates";
import { Template } from "PFTypes";
import { useCallback } from "react";

import { useValueRenderer } from "./use_value_renderer";

type UsePropertyRendererReturn = (
  template: Template,
  name: string,
  value: any,
  valueType: string | null,
  separator?: string
) => React.ReactElement | string;

export const usePropertyRenderer = (): UsePropertyRendererReturn => {
  const stringifyFunctions = useValueRenderer();

  return useCallback(
    (template, name, value, valueType = null, separator = "\n") => {
      const property = getProperties(template).find((prop) => prop.name === name);
      if (!property) {
        return "";
      }
      let { type } = property;
      const { description } = property;

      // 'custom_field' of the valueType 'date' and 'boolean' are a special case that our 'custom_field'
      //  stringify function can't properly handle. We want to format them using the
      //  'date'/'toggle' serailizer.
      if (type === "custom_field" && valueType === "date") {
        type = "date";
        const firstValue: any = first(value);
        value = firstValue?.value ?? "";
      }

      if (type === "custom_field" && valueType === "boolean") {
        type = "toggle";
        const firstValue: any = first(value);
        value = firstValue?.value ?? "";
      }

      if (type === "checkboxes" && every(value, ({ value }) => !value)) {
        return "";
      }

      const stringify = type ? stringifyFunctions[type] : null;
      const out = (stringify && stringify({ value, description, separator, name })) || String(value) || "";

      if (type === "link") {
        const index = (out as string).indexOf("://");
        return 0 >= index || index > 8 ? `http://${out}` : out; // let's add http if no link protocol provided
      } else if (isArray(out)) {
        return out.join(separator);
      } else {
        return out;
      }
    },
    [stringifyFunctions]
  );
};
